import type { ReactNode } from 'react'
import { useCallback, useState } from 'react'
import * as Sentry from '@sentry/nextjs'
import type { Emoji } from 'emoji-mart'
import dynamic from 'next/dynamic'
import { usePopper } from 'react-popper'
import { twMerge } from 'tailwind-merge'
import { Loader } from 'shared/components/loader'
import { useClickOutside } from 'shared/hooks/use-click-outside'
import EmojiOutlineIcon from 'shared/icons/emoji-outline-icon'
import { ToolbarButton } from 'modules/comments/components/comment-editor/common/toolbar-button'

const EmojiPickerLoader = (): ReactNode => (
  <div className="flex size-64 items-center justify-center rounded-lg bg-background-secondary shadow-xl">
    <Loader />
  </div>
)

const Picker = dynamic(async () => import('@emoji-mart/react'), {
  loading: () => <EmojiPickerLoader />,
})

interface EmojiPickerProps {
  onEmojiSelect: (emoji: typeof Emoji.Props) => void
  iconClassName?: string
  pickerClassName?: string
}

export const EmojiPicker = ({
  onEmojiSelect,
  pickerClassName = '',
  iconClassName = '',
}: EmojiPickerProps): ReactNode => {
  const [isPickerVisible, setIsPickerVisible] = useState(false)
  const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(null)
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null)
  // TODO: Define proper type for emojiData
  const [emojiData, setEmojiData] = useState<unknown>(null)

  const { styles: popperStyles, attributes: popperAttributes } = usePopper(
    referenceElement,
    popperElement,
    {
      placement: 'top',
      strategy: 'fixed',
      modifiers: [
        {
          name: 'flip',
          options: {
            fallbackPlacements: ['top', 'bottom'],
          },
        },
        {
          name: 'offset',
          options: {
            offset: [0, 6],
          },
        },
      ],
    },
  )

  const toggleEmojiPicker = useCallback(() => {
    setIsPickerVisible(prev => !prev)
  }, [])

  useClickOutside(toggleEmojiPicker, null, [popperElement, referenceElement])

  const loadEmojiPicker = async (): Promise<void> => {
    if (emojiData) return

    try {
      const response = await fetch('https://cdn.jsdelivr.net/npm/@emoji-mart/data', {
        headers: {
          Accept: 'application/json',
        },
      })
      setEmojiData(await response.json())
    } catch (error) {
      Sentry.captureException(error, {
        extra: { context: 'Failed to load emoji picker' },
      })
    }
  }

  return (
    <>
      <ToolbarButton ref={setReferenceElement} type="button" className="hidden md:block">
        <EmojiOutlineIcon
          className={twMerge(
            `cursor-pointer text-text-secondary hover:text-text-primary`,
            iconClassName,
          )}
          onClick={toggleEmojiPicker}
          onMouseEnter={loadEmojiPicker}
        />
      </ToolbarButton>

      {isPickerVisible && (
        <div
          ref={setPopperElement}
          style={{ ...popperStyles.popper }}
          className={twMerge(pickerClassName, 'z-50')}
          {...popperAttributes.popper}
        >
          {emojiData ? (
            <Picker
              data={emojiData}
              onEmojiSelect={(emoji: typeof Emoji.Props) => {
                onEmojiSelect(emoji)
                setIsPickerVisible(false)
              }}
            />
          ) : (
            <EmojiPickerLoader />
          )}
        </div>
      )}
    </>
  )
}
